import { LockClosedIcon } from '@heroicons/react/solid';
import { BigNumber, constants } from 'ethers';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useContractWrite, usePrepareContractWrite, useWaitForTransaction } from 'wagmi';
import { gmooContract, gmooABI, keekContract, keekABI } from '../../lib/util/addresses';
import { toastSuccess } from '../../lib/util/toast';
import { LoadingIcon } from '../Icons';
import { CollectionType } from '../../lib/util/types';

type Props = {
  id: number;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

export const LockBasicMoo = ({ id, setOpen }: Props) => {
  const [loading, setLoading] = useState(false);

  const { config } = usePrepareContractWrite({
    address: gmooContract,
    abi: gmooABI,
    functionName: 'lockMoo',
    args: [BigNumber.from(id), BigNumber.from(0), constants.HashZero],
  });

  const { write: lock, data, isSuccess, isError } = useContractWrite(config);

  const { isSuccess: lockSuccess } = useWaitForTransaction({
    hash: data?.hash,
  });

  useEffect(() => {
    if ((isSuccess && lockSuccess) || isError) {
      setLoading(false);
      setOpen(false);
      lockSuccess && toastSuccess('Locked!');
    }
  }, [isSuccess, setLoading, lockSuccess, isError, setOpen]);

  const onClick = () => {
    setLoading(true);
    lock?.();
  };

  return <Shared onClick={onClick} loading={loading} type="gmoo" />;
};

export const LockBasicKeek = ({ id, setOpen }: Props) => {
  const [loading, setLoading] = useState(false);

  const { config } = usePrepareContractWrite({
    address: keekContract,
    abi: keekABI,
    functionName: 'lockKeek',
    args: [BigNumber.from(id), BigNumber.from(0), constants.HashZero],
  });

  const { write: lock, data, isSuccess, isError } = useContractWrite(config);

  const { isSuccess: lockSuccess } = useWaitForTransaction({
    hash: data?.hash,
  });

  useEffect(() => {
    if ((isSuccess && lockSuccess) || isError) {
      setLoading(false);
      setOpen(false);
      lockSuccess && toastSuccess('Locked!');
    }
  }, [isSuccess, setLoading, lockSuccess, isError, setOpen]);

  const onClick = () => {
    setLoading(true);
    lock?.();
  };

  return <Shared onClick={onClick} loading={loading} type="keek" />;
};

type SharedProps = {
  onClick: () => void;
  loading: boolean;
  type: CollectionType;
};

const Shared = ({ onClick, loading, type }: SharedProps) => {
  const name = type === 'gmoo' ? 'Moo' : 'Keek';

  return (
    <div className="mt-6 flex flex-col items-center justify-center gap-6">
      <button
        className="flex items-center gap-2 rounded-lg bg-purple py-2 pl-4 pr-6"
        onClick={onClick}
        disabled={loading}
      >
        {!loading && <LockClosedIcon className="h-8 w-8 text-white" />}
        {loading && <LoadingIcon className="h-8 w-8 text-white" />}
        <span className="font-gmcafe text-3xl text-white">Lock</span>
      </button>
      <div className="text-purple">
        <h3 className="font-semibold">What does it mean to lock my {name}?</h3>
        <ul className="list-inside list-disc text-sm">
          <li>If you try to transfer a locked {name}, the transaction will fail.</li>
          <li>If your {name} is listed on a marketplace, no one will be able to purchase it.</li>
        </ul>
      </div>
      <div className="text-purple">
        <h3 className="font-semibold">Does this mean my {name} is 100% safe?</h3>
        <p className="text-sm">
          No. If you have already signed away access to your {name}, then you should make plans to
          transfer your {name} to a separate wallet when the coast is clear. With the basic lock
          function, the thief would be able to access this UI, unlock the {name} manually, then
          transfer the {name} elsewhere.
        </p>
      </div>
    </div>
  );
};
